import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, Spinner, Badge, Button } from "../../shared/ui";
import { Activity, Calendar, Clock, MapPin, Truck, User as UserIcon, AlertCircle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  quoted_job: { label: "Quoted Job", color: "bg-blue-100 text-blue-800 border-blue-300", icon: "📋" },
  general_hire: { label: "General Hire", color: "bg-amber-100 text-amber-800 border-amber-300", icon: "📝" },
  dry_hire: { label: "Dry Hire", color: "bg-purple-100 text-purple-800 border-purple-300", icon: "🔑" },
  travel: { label: "Travel", color: "bg-slate-100 text-slate-700 border-slate-300", icon: "🚛" },
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
};

/**
 * Unified per-machine activity timeline — combines machine_usage, dry hires,
 * general hires, and travel into one chronological feed. Money fields are
 * stripped server-side for non-admin/manager/office viewers.
 */
export const MachineTimelineTab = ({ machineId, currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const canSeeMoney = ["admin", "manager", "office_staff"].includes(currentUser?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/machines/${machineId}/timeline`);
      setData(r.data);
    } catch (e) {
      console.error("Couldn't load timeline", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [machineId]);

  if (loading) return <Spinner />;
  if (!data) return <div className="text-slate-500 italic">No data</div>;

  const items = (data.items || []).filter((i) => filter === "all" || i.kind === filter);

  return (
    <div className="space-y-4" data-testid="machine-timeline-tab">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold uppercase text-base flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" /> Activity Timeline
          </h3>
          <Button size="sm" variant="ghost" onClick={fetchData} data-testid="timeline-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <div className="p-2 bg-slate-50 border border-slate-200 rounded">
            <div className="text-[10px] uppercase font-bold text-slate-500">Total Records</div>
            <div className="text-xl font-bold">{data.total_records}</div>
          </div>
          <div className="p-2 bg-orange-50 border border-orange-200 rounded">
            <div className="text-[10px] uppercase font-bold text-orange-700">Total Hours</div>
            <div className="text-xl font-bold text-orange-700">{data.total_hours}</div>
          </div>
          <div className="p-2 bg-blue-50 border border-blue-200 rounded">
            <div className="text-[10px] uppercase font-bold text-blue-700">Total KM</div>
            <div className="text-xl font-bold text-blue-700">{data.total_kms}</div>
          </div>
          {Object.entries(data.by_kind || {}).map(([k, v]) => (
            <div key={k} className={`p-2 rounded border ${KIND_LABELS[k]?.color || "bg-slate-50 border-slate-200"}`}>
              <div className="text-[10px] uppercase font-bold">{KIND_LABELS[k]?.label || k}</div>
              <div className="text-xl font-bold">{v}</div>
            </div>
          ))}
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-1 mb-3" data-testid="timeline-filters">
          {[["all", "All"], ["quoted_job", "Quoted Jobs"], ["general_hire", "General Hires"], ["dry_hire", "Dry Hires"], ["travel", "Travel"]].map(([code, lbl]) => (
            <button key={code} onClick={() => setFilter(code)}
              data-testid={`timeline-filter-${code}`}
              className={`px-3 py-1 text-xs font-bold uppercase rounded-full border ${
                filter === code ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>{lbl}</button>
          ))}
        </div>

        {!items.length ? (
          <p className="text-center text-slate-400 italic py-6">No activity yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((i) => {
              const kindMeta = KIND_LABELS[i.kind] || { label: i.kind, color: "bg-slate-50 border-slate-200", icon: "·" };
              return (
                <div key={`${i.kind}-${i.id}`} className={`p-3 rounded-lg border ${i.status === "active" || i.status === "in_progress" || i.status === "out_on_hire" ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`timeline-item-${i.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={kindMeta.color}>{kindMeta.icon} {kindMeta.label}</Badge>
                        {i.status && <Badge variant={i.status === "completed" || i.status === "returned" ? "success" : "warning"}>{i.status}</Badge>}
                        <span className="font-bold text-sm">{i.title}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-1">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(i.date)}{i.end_date && ` → ${fmtDate(i.end_date)}`}</span>
                        {i.operator_name && <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" />{i.operator_name}</span>}
                        {i.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{i.location}</span>}
                        {i.total_hours != null && i.total_hours > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{Number(i.total_hours).toFixed(2)}h</span>}
                        {i.total_kms != null && i.total_kms > 0 && <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{Number(i.total_kms).toFixed(1)}km</span>}
                      </div>
                      {(i.starting_hours != null || i.ending_hours != null) && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          Hours: {i.starting_hours ?? "—"} → {i.ending_hours ?? "—"}
                          {(i.starting_kms != null || i.ending_kms != null) && <span className="ml-2">· KM: {i.starting_kms ?? "—"} → {i.ending_kms ?? "—"}</span>}
                        </div>
                      )}
                      {i.kind === "dry_hire" && (i.start_photos_count || i.return_photos_count) && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          📷 Start photos: {i.start_photos_count || 0} · Return photos: {i.return_photos_count || 0}
                        </div>
                      )}
                    </div>
                    {canSeeMoney && i.rate_amount && (
                      <div className="text-right text-xs">
                        <div className="font-bold text-green-700">${i.rate_amount}/{i.rate_type || ""}</div>
                        {i.damage_charge ? <div className="text-red-600">Damage: ${i.damage_charge}</div> : null}
                        {i.cleaning_charge ? <div className="text-red-600">Clean: ${i.cleaning_charge}</div> : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!canSeeMoney && (
          <div className="mt-3 text-[11px] italic text-slate-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> $ amounts and rates are hidden for your role.
          </div>
        )}
      </Card>
    </div>
  );
};

export default MachineTimelineTab;
